import React from 'react';
import { useTranslation } from 'react-i18next';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Copy } from 'lucide-react';
import type { EventDressCode, Gender, AgeCategory, UpdateDressCodeDto } from '@/types/dressCode.types';

interface Props {
  gender: Gender;
  ageCategory: AgeCategory;
  entry: EventDressCode | undefined;
  onChange: (gender: Gender, ageCategory: AgeCategory, dto: UpdateDressCodeDto & { description: string }) => void;
  onCopyToAll: (gender: Gender, ageCategory: AgeCategory) => void;
  celebrationId: number;
  disabled?: boolean;
}

const DressCodeCell: React.FC<Props> = ({
  gender,
  ageCategory,
  entry,
  onChange,
  onCopyToAll,
  celebrationId,
  disabled = false,
}) => {
  const { t } = useTranslation('events');
  const [description, setDescription] = React.useState(entry?.description ?? '');
  const [avoid, setAvoid] = React.useState(entry?.avoid ?? '');

  // Refs hold the latest values so handleBlur never reads a stale closure
  const descriptionRef = React.useRef(description);
  const avoidRef = React.useRef(avoid);

  // Sync if parent entry changes (e.g. after template apply)
  React.useEffect(() => {
    const d = entry?.description ?? '';
    const a = entry?.avoid ?? '';
    setDescription(d);
    setAvoid(a);
    descriptionRef.current = d;
    avoidRef.current = a;
  }, [entry?.description, entry?.avoid]);

  function handleBlur() {
    onChange(gender, ageCategory, {
      celebration_id: celebrationId,
      description: descriptionRef.current,
      avoid: avoidRef.current || undefined,
    });
  }

  return (
    <div className="space-y-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
      <div className="space-y-1">
        <Label className="text-xs text-gray-500 font-medium">{t('dressCode.cell.whatToWear')}</Label>
        <Textarea
          placeholder={t('dressCode.cell.describePlaceholder')}
          value={description}
          onChange={e => { setDescription(e.target.value); descriptionRef.current = e.target.value; }}
          onBlur={handleBlur}
          disabled={disabled}
          rows={3}
          className="text-xs resize-none"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-red-500 font-medium">{t('dressCode.cell.whatToAvoid')}</Label>
        <Textarea
          placeholder={t('dressCode.cell.avoidPlaceholder')}
          value={avoid}
          onChange={e => { setAvoid(e.target.value); avoidRef.current = e.target.value; }}
          onBlur={handleBlur}
          disabled={disabled}
          rows={2}
          className="text-xs resize-none border-red-100 focus:border-red-300"
        />
      </div>

      {!disabled && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full h-6 text-xs text-gray-400 hover:text-gray-700 gap-1"
          onClick={() => onCopyToAll(gender, ageCategory)}
          title={t('dressCode.cell.copyTitle')}
        >
          <Copy className="w-3 h-3" /> {t('dressCode.cell.copyToAll')}
        </Button>
      )}
    </div>
  );
};

export default DressCodeCell;
